import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageTransition from '@/components/PageTransition';
import Icon from '@/components/ui/AppIcon';
import EventBookingCalendar from '@/app/events/components/EventBookingCalendar';
import EventVenueList from '@/app/events/components/EventVenueList';
import TryBookingWidget from '@/app/events/components/TryBookingWidget';
import { fetchEventBySlug, mediaUrl } from '@/lib/api';
import { getEventSessions } from '@/lib/eventSessions';
import {
  hasTryBookingWidget,
  resolveTryBookingEventId,
  resolveVenueTryBookingEventId,
  venueUsesTryBookingWidget,
} from '@/data/trybookingEvents';

const categoryColors = {
  Theatre: 'bg-purple-100 text-purple-800 border-purple-300',
  Film: 'bg-blue-100 text-blue-800 border-blue-300',
  Music: 'bg-amber-100 text-amber-800 border-amber-300',
};

function EventInfoCards({ event }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="rounded-xl border border-border/60 p-4">
        <div className="flex items-center gap-2 text-primary mb-2">
          <Icon name="CalendarDaysIcon" size={16} />
          <span className="text-xs font-bold uppercase tracking-wide">Date</span>
        </div>
        <p className="font-semibold text-foreground">{event.date}</p>
      </div>
      <div className="rounded-xl border border-border/60 p-4">
        <div className="flex items-center gap-2 text-primary mb-2">
          <Icon name="TicketIcon" size={16} />
          <span className="text-xs font-bold uppercase tracking-wide">From</span>
        </div>
        <p className="font-semibold text-primary text-lg">{event.price}</p>
      </div>
    </div>
  );
}

export default function EventDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const bookIndex = searchParams.get('book');
  const selectedVenueIndex = bookIndex != null && bookIndex !== '' ? Number(bookIndex) : null;

  useEffect(() => {
    let active = true;
    setLoading(true);

    fetchEventBySlug(slug)
      .then((data) => {
        if (active) setEvent(data);
      })
      .catch(() => {
        if (active) navigate('/404', { replace: true });
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [slug, navigate]);

  useEffect(() => {
    if (!event || hasTryBookingWidget(event) || (event.venues?.length ?? 0) > 1) return undefined;
    setSessions(getEventSessions(event));
    return undefined;
  }, [event]);

  if (loading) {
    return (
      <main className="min-h-screen bg-background">
        <Header />
        <p className="max-w-4xl mx-auto px-4 py-24 text-muted-foreground">Loading...</p>
      </main>
    );
  }

  if (!event) return null;

  const hasMultipleVenues = (event.venues?.length ?? 0) > 1 || (event.hasMultipleLocations && (event.venues?.length ?? 0) > 0);
  const selectedVenue = hasMultipleVenues && Number.isInteger(selectedVenueIndex)
    ? event.venues[selectedVenueIndex]
    : null;
  const selectedVenueUsesWidget = selectedVenue ? venueUsesTryBookingWidget(selectedVenue) : false;
  const trybookingEventId = selectedVenue
    ? resolveVenueTryBookingEventId(selectedVenue)
    : resolveTryBookingEventId(event);
  const usesTryBookingWidget = selectedVenue
    ? selectedVenueUsesWidget
    : hasTryBookingWidget(event);

  function handleVenueBook(venue, index) {
    if (venueUsesTryBookingWidget(venue)) {
      navigate(`/events/${slug}?book=${index}`);
      return;
    }

    if (venue.ticketUrl) {
      window.location.href = venue.ticketUrl;
    }
  }

  if (usesTryBookingWidget) {
    const sessionLabel = selectedVenue
      ? [selectedVenue.location, selectedVenue.date, selectedVenue.time].filter(Boolean).join(' · ')
      : event.date;

    return (
      <main className="min-h-screen bg-background">
        <Header />
        <PageTransition>
          <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            {hasMultipleVenues ? (
              <div className="mb-4">
                <Link
                  to={`/events/${slug}`}
                  className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
                >
                  <Icon name="ArrowLeftIcon" size={14} />
                  Back to all venues
                </Link>
              </div>
            ) : null}
            <TryBookingWidget
              eventId={trybookingEventId}
              widgetType="landingPageEmbed"
              sessionLabel={sessionLabel}
              backHref={hasMultipleVenues ? `/events/${slug}` : '/events'}
              backLabel={hasMultipleVenues ? 'Back to all venues' : 'Back to events'}
              className="rounded-xl border border-border/70 shadow-sm"
            />
          </section>
          <Footer />
        </PageTransition>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background">
      <Header />
      <PageTransition>
        <section className="relative">
          <div className="relative h-[320px] sm:h-[420px] overflow-hidden">
            <img
              src={mediaUrl(event.image)}
              alt={event.alt}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
              <Link
                to="/events"
                className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-4"
              >
                <Icon name="ArrowLeftIcon" size={14} />
                Back to events
              </Link>
              <span
                className={`inline-flex text-xs font-semibold px-2.5 py-1 rounded-full border mb-3 ${
                  categoryColors[event.category] || 'bg-white/10 text-white border-white/20'
                }`}
              >
                {event.category}
              </span>
              <p className="text-primary text-sm font-bold uppercase tracking-wide">
                {hasMultipleVenues
                  ? `${event.venues.length} locations across Australia`
                  : event.date}
              </p>
              <h1 className="font-display text-3xl sm:text-5xl font-semibold text-white mt-2">
                {event.title}
              </h1>
              <p className="text-white/80 mt-2 text-lg">{event.subtitle}</p>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {hasMultipleVenues ? (
            <div className="space-y-10">
              <div>
                <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
                  About this event
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {event.description || `${event.title} — ${event.subtitle}.`}
                </p>
              </div>

              <EventInfoCards event={event} />

              <div id="venues">
                <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
                  Select your location
                </h2>
                <EventVenueList venues={event.venues} onBook={handleVenueBook} />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="space-y-8">
                <div>
                  <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
                    About this event
                  </h2>
                  <p className="text-muted-foreground leading-relaxed">
                    {event.description || `${event.title} — ${event.subtitle}.`}
                  </p>
                </div>
                <EventInfoCards event={event} />
              </div>

              <div>
                <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
                  Choose your date
                </h2>
                <EventBookingCalendar
                  eventTitle={event.title}
                  sessions={sessions}
                  ticketUrl={event.ticketUrl}
                />
              </div>
            </div>
          )}
        </section>
        <Footer />
      </PageTransition>
    </main>
  );
}
